import plusTealIcon from "@assets/images/plus-teal.svg";
import saveIcon from "@assets/images/save-icon.svg";
import { Button, Paper } from "@material-ui/core";
import DecimalFormat from "@saleor/components/DecimalFormat";
import MyTypography from "@saleor/components/MyTypography";
import { menuWidth, shrunkMenuWidth } from "@saleor/components/Siderbar";
import { ChangeOrderCSTab } from "@saleor/createOrderDomestic/views/CreateOrderView/types";
import useAppState from "@saleor/hooks/useAppState";
import { makeStyles } from "@saleor/macaw-ui";
import {
  formatThousandDecimal,
  shortenLongDecimal
} from "@saleor/utils/number";
import classNames from "classnames";
import React from "react";
import InlineSVG from "react-inlinesvg";
import { FormattedMessage, useIntl } from "react-intl";

interface propsType {
  expandMenu?: boolean;
  fullScreen?: boolean;
  bodyPaddingRight?: number;
}

const useStyles = makeStyles<propsType>(
  theme => ({
    menu: {
      zIndex: 100,
      padding: 0,
      position: "fixed",
      bottom: 0,
      right: 0,
      transition: "width 0.4s ease",
      width: "100%",
      [theme.breakpoints.up("md")]: {
        padding: "0 2.4rem",
        paddingRight: props => `calc(2.4rem + ${props.bodyPaddingRight}px)`,
        width: props => {
          if (props.fullScreen) {
            return "100%";
          }
          return props.expandMenu
            ? `calc(100% - ${menuWidth}px)`
            : `calc(100% - ${shrunkMenuWidth}px)`;
        }
      }
    },
    wrapper: {
      display: "block",
      width: "100%",
      alignItems: "center",
      boxShadow: "0px -8px 24px rgb(0 0 0 / 8%)",
      borderRadius: "20px 20px 0px 0px"
    },
    section: {
      display: "flex",
      padding: "1.6rem 2.4rem",
      justifyContent: "space-between",
      gap: "0 2.4rem"
    },
    divider: {
      margin: 0,
      border: 0,
      borderTop: `1px dashed ${theme.palette.grey[500]}`
    },
    summaryCaption: {
      color: theme.palette.grey[900]
    },
    number: {
      fontSize: "1.8rem",
      lineHeight: "2.4rem"
    },
    sectionRight: {
      display: "flex"
    },
    price: {
      minWidth: 152,
      marginRight: "2.4rem",
      "&:last-child": {
        marginRight: 0,
        minWidth: "75px"
      }
    },
    priceCaption: {
      color: theme.palette.grey[600],
      marginBottom: "0.6rem"
    },
    netPriceNumber: {
      color: theme.palette.info.dark
    },
    btnSaveDraft: {
      alignItems: "center",
      height: 48,
      padding: "0 13px",
      "& .MuiButton-label": {
        fontWeight: 700,
        gap: "0 8px"
      }
    },
    btnConfirmOrder: {
      color: "#ffffff",
      alignItems: "center",
      fontSize: "1.8rem",
      lineHeight: "2.8rem",
      height: 48,
      padding: "0 73px 0 74px",
      "& .MuiButton-label": {
        fontWeight: 700
      }
    },
    leftContainer: {
      display: "flex",
      alignItems: "center",
      "& button": {
        padding: "12px 27px",
        marginRight: "1.6rem"
      }
    },
    deleteItem: { color: "inherit !important" },
    btnAddProduct: {
      "& .MuiButton-label": {
        fontWeight: 700,
        fontSize: "1.6rem"
      }
    },
    btnDelete: {
      height: 48,
      fontSize: "1.6rem",
      lineHeight: "24px",
      minWidth: 160
    }
  }),
  { name: "BottomBarCreateOrder" }
);

interface BottomBarCreateOrderProps {
  onAddItem: () => void;
  priceData: {
    itemCount: number;
    totalPrice: number;
    vat: number;
    netPrice: number;
    usd: string;
  };
  deleteCount: number;
  activeTab: ChangeOrderCSTab;
  onDelete: () => void;
  onSaveDraft: () => void;
  onSubmitOrder: () => void;
  isDisableBtnCreateOrder: boolean;
  isDisableBtnSaveDraft?: boolean;
  taxPercent: number;
}

const BottomBarCreateOrder: React.FC<BottomBarCreateOrderProps> = ({
  onAddItem,
  priceData,
  onDelete,
  onSaveDraft,
  onSubmitOrder,
  deleteCount,
  activeTab,
  isDisableBtnCreateOrder,
  isDisableBtnSaveDraft,
  taxPercent
}) => {
  const [appState] = useAppState();
  const classes = useStyles({
    expandMenu: appState.expandMenu,
    fullScreen: appState.fullScreen,
    bodyPaddingRight: appState.bodyPaddingRight
  });
  const intl = useIntl();
  const { itemCount, totalPrice, vat, netPrice } = priceData;

  return (
    <div className={classes.menu} data-testid="bottomBar">
      <Paper className={classes.wrapper}>
        <div className={classes.section}>
          <div>
            <MyTypography
              variant="subtitle2"
              className={classes.summaryCaption}
            >
              {intl.formatMessage({
                defaultMessage: "สรุปยอดคำสั่งซื้อ"
              })}
            </MyTypography>
            <MyTypography
              variant="body1"
              className={classes.number}
              data-testid="itemCount"
            >
              ({itemCount}{" "}
              {itemCount >= 2
                ? intl.formatMessage({ defaultMessage: "items" })
                : intl.formatMessage({ defaultMessage: "item" })}
              )
            </MyTypography>
          </div>
          <div className={classes.sectionRight}>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "ยอดรวมสุทธิ"
                })}
              </MyTypography>
              <MyTypography
                variant="body1"
                className={classes.number}
                data-testid="netPrice"
              >
                {netPrice ? (
                  <DecimalFormat
                    value={netPrice}
                    isGrayDecimal={false}
                    countDecimal={2}
                  />
                ) : (
                  "0.00"
                )}
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {`ภาษีมูลค่าเพิ่ม ${shortenLongDecimal(taxPercent * 100)}%`}
              </MyTypography>
              <MyTypography
                variant="body1"
                className={classes.number}
                data-testid="vat"
              >
                {vat ? (
                  <DecimalFormat
                    value={vat}
                    isGrayDecimal={false}
                    countDecimal={2}
                  />
                ) : (
                  "0.00"
                )}
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "ยอดรวมภาษีมูลค่าเพิ่ม"
                })}
              </MyTypography>
              <MyTypography
                variant="subtitle1"
                className={classes.netPriceNumber}
                data-testid="totalPrice"
              >
                <span className={classes.totalPrice}>
                  {totalPrice
                    ? formatThousandDecimal(
                        Number(vat.toFixed(2)) + Number(netPrice.toFixed(2)),
                        2
                      )
                    : "0.00"}
                </span>
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "Currency"
                })}
              </MyTypography>
              <MyTypography
                variant="body1"
                className={classes.number}
                data-testid="currency"
              >
                {priceData.usd}
              </MyTypography>
            </div>
          </div>
        </div>
        <hr className={classes.divider} />
        <div className={classes.section}>
          <div className={classes.leftContainer}>
            {activeTab === ChangeOrderCSTab.itemDetails && (
              <>
                <Button
                  id="deleteItems"
                  variant="outlined"
                  color="primary"
                  onClick={onDelete}
                  className={classNames(classes.btnDelete)}
                  disabled={deleteCount === 0}
                  data-testid="delete-items-button"
                >
                  {deleteCount > 1 ? (
                    <FormattedMessage defaultMessage="Delete items" />
                  ) : (
                    <FormattedMessage defaultMessage="Delete item" />
                  )}
                  &nbsp;(
                  <MyTypography
                    variant="subtitle2"
                    className={classes.deleteItem}
                  >
                    <b>{deleteCount}</b>
                  </MyTypography>
                  )
                </Button>
                <Button
                  id="addItem"
                  variant="outlined"
                  color="secondary"
                  onClick={onAddItem}
                  className={classes.btnAddProduct}
                  disabled={false}
                  startIcon={<InlineSVG src={plusTealIcon} />}
                >
                  <span>
                    <FormattedMessage defaultMessage="เพิ่มรายการสินค้า" />
                  </span>
                </Button>
              </>
            )}
          </div>
          <div className={classes.leftContainer}>
            <Button
              id="saveDraft"
              variant="outlined"
              color="secondary"
              onClick={onSaveDraft}
              className={classes.btnSaveDraft}
              startIcon={<InlineSVG src={saveIcon} />}
              disabled={isDisableBtnSaveDraft}
            >
              <span>
                <FormattedMessage defaultMessage="บันทึกฉบับร่าง" />
              </span>
            </Button>
            <Button
              id="saveOrder"
              variant="contained"
              color="secondary"
              onClick={onSubmitOrder}
              disabled={isDisableBtnCreateOrder}
              className={classes.btnConfirmOrder}
            >
              <FormattedMessage defaultMessage="ตกลงสั่งซื้อ" />
            </Button>
          </div>
        </div>
      </Paper>
    </div>
  );
};

export default BottomBarCreateOrder;
